import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FormTextField, ReactSelectForm, Toggle } from '../../../../../components';
import { Court } from '../../../../../types/tournament';
import { ROUND_KEYS } from '../schedule.helpers';

interface GlobalRulesSetupProps {
  courts: Court[];
}

export const GlobalRulesSetup = ({ courts }: GlobalRulesSetupProps) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  const courtOptions = useMemo(() => {
    return courts.map((c) => ({
      label: c.name,
      value: c.id.toString(),
    }));
  }, [courts]);

  return (
    <Container fluid className="px-0">
      <Row>
        <Col md={6} className="mb-4">
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0 fw-bold">{t(prefix + 'broadcastRoutingTitle')}</h5>
            </Card.Header>
            <Card.Body className="bg-light">
              <Row className="h-100">
                <Col md={6} className="mb-3 d-flex flex-column">
                  <label className="form-label fw-bold">
                    {t(prefix + 'centerStreamCourtsLabel')}
                  </label>
                  <div className="text-muted small mb-2">
                    {t(prefix + 'centerStreamCourtsDesc')}
                  </div>

                  <div className="mt-auto">
                    {/* Invisible spacer label to perfectly match the height of the labels on the right side */}
                    <label className="form-label invisible mb-2 d-block">Spacer</label>
                    <ReactSelectForm
                      name="stream_courts"
                      options={courtOptions}
                      isMulti
                      placeholder={t(prefix + 'selectCourtsPlaceholder')}
                    />
                  </div>
                </Col>

                <Col md={6} className="mb-3 d-flex flex-column">
                  <label className="form-label fw-bold">{t(prefix + 'matchChunkingTitle')}</label>
                  <div className="text-muted small mb-2">{t(prefix + 'matchChunkingDesc')}</div>

                  <div className="mt-auto">
                    <Row>
                      <Col xs={6}>
                        <FormTextField
                          type="number"
                          name="max_chunk_size"
                          label={t(prefix + 'maxChunkSizeLabel')}
                          placeholder="2"
                        />
                      </Col>
                      <Col xs={6}>
                        <FormTextField
                          type="number"
                          name="number_of_chunk_splits"
                          label={t(prefix + 'allowedSplitsLabel')}
                          placeholder="1"
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </Card.Body>

            <Card.Header className="bg-white py-3 border-top">
              <h5 className="mb-0 fw-bold">{t(prefix + 'tournamentPreferencesTitle')}</h5>
            </Card.Header>
            <Card.Body className="bg-light">
              <div className="d-flex align-items-center mb-4">
                <Toggle name="allow_split" />
                <div className="ms-3">
                  <strong className="d-block mb-1 cursor-pointer">
                    {t(prefix + 'allowMatchSplittingLabel')}
                  </strong>
                  <div className="text-muted small">{t(prefix + 'allowMatchSplittingDesc')}</div>
                </div>
              </div>

              <div className="d-flex align-items-center mb-4">
                <Toggle name="allow_alignment" />
                <div className="ms-3">
                  <strong className="d-block mb-1 cursor-pointer">
                    {t(prefix + 'forceCourtAlignmentLabel')}
                  </strong>
                  <div className="text-muted small">{t(prefix + 'forceCourtAlignmentDesc')}</div>
                </div>
              </div>

              <div className="d-flex align-items-center mb-4">
                <Toggle name="pull_to_lower_court" />
                <div className="ms-3">
                  <strong className="d-block mb-1 cursor-pointer">
                    {t(prefix + 'pullToLowerCourtsLabel')}
                  </strong>
                  <div className="text-muted small">{t(prefix + 'pullToLowerCourtsDesc')}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0 fw-bold">{t(prefix + 'solverEffortTitle')}</h5>
              <div className="text-muted small mt-1">{t(prefix + 'solverEffortDesc')}</div>
            </Card.Header>
            <Card.Body className="bg-light">
              <Row>
                <Col xs={6}>
                  <FormTextField
                    type="number"
                    name="s1_time_limit"
                    label={t(prefix + 'phase1LimitLabel')}
                  />
                </Col>
                <Col xs={6}>
                  <FormTextField
                    type="number"
                    name="s2_time_limit"
                    label={t(prefix + 'phase2LimitLabel')}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0 fw-bold">{t(prefix + 'restingGapsTitle')}</h5>
              <div className="text-muted small mt-1">{t(prefix + 'restingGapsDesc')}</div>
            </Card.Header>
            <Card.Body className="bg-light">
              <Row className="text-center mb-2 fw-bold d-none d-md-flex text-muted">
                <Col xs={4} className="text-start">
                  {t(prefix + 'matchSequenceLabel')}
                </Col>
                <Col xs={4}>{t(prefix + 'minGapLabel')}</Col>
                <Col xs={4}>{t(prefix + 'maxGapLabel')}</Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col md={4} className="fw-bold">
                  {t(prefix + 'groupGroupMatch')}
                </Col>
                <Col md={4}>
                  <FormTextField type="number" name="gap_group_group" />
                </Col>
                <Col md={4}>
                  <FormTextField type="number" name="max_gap_group_group" />
                </Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col md={4} className="fw-bold">
                  {t(prefix + 'groupKoMatch')}
                </Col>
                <Col md={4}>
                  <FormTextField type="number" name="gap_group_ko" />
                </Col>
                <Col md={4}>
                  <FormTextField type="number" name="max_gap_group_ko" />
                </Col>
              </Row>

              <Row className="align-items-center mb-4 border-bottom pb-4">
                <Col md={4} className="fw-bold">
                  {t(prefix + 'koKoMatch')}
                </Col>
                <Col md={4}>
                  <FormTextField type="number" name="gap_ko_ko" />
                </Col>
                <Col md={4}>
                  <FormTextField type="number" name="max_gap_ko_ko" />
                </Col>
              </Row>

              <Row className="align-items-center">
                <Col md={4} className="fw-bold">
                  {t(prefix + 'maxTeamSpanLabel')} <br />
                  <span className="text-muted small fw-normal">
                    {t(prefix + 'maxTeamSpanDesc')}
                  </span>
                </Col>
                <Col md={4}>
                  <FormTextField
                    type="number"
                    name="max_span_limit"
                    label={t(prefix + 'hoursLabel')}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0 fw-bold">
                {t(prefix + 'globalMatchDurationsTitle', 'Global Match Durations (Minutes)')}
              </h5>
              <div className="text-muted small mt-1">
                {t(
                  prefix + 'globalMatchDurationsDesc',
                  'Set the default duration for matches in each round. You can override these for specific classes in the Classes & Formats tab.'
                )}
              </div>
            </Card.Header>
            <Card.Body className="bg-light">
              <Row>
                {ROUND_KEYS.map((round) => (
                  <Col md={3} sm={4} xs={6} key={round.key} className="mb-3">
                    <FormTextField
                      type="number"
                      name={`globalMatchLength_${round.key}`}
                      label={round.label}
                    />
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
